import React from 'react';

import { OptimizelyProvider, type UserInfo } from '@optimizely/react-sdk';

import { optimizelyClient } from './client';

export type OptimizelyWrapperPropsT = {
  children: React.ReactNode;
  timeoutMs?: number;
  user?: UserInfo;
};

export type OptimizelyWrapperT = React.FC<OptimizelyWrapperPropsT>;

/**
 * How long to wait for the client to become ready before giving up and
 * reporting an error, which resolves every `useFeatureFlag` call to off.
 *
 * The SDK's own default is 30 seconds. That is far too long for a caller that
 * withholds UI until the decision lands: a CDN that accepts the connection and
 * then stalls produces a 30-second blank page. The datafile is a small JSON
 * document from a CDN, so three seconds is already generous, and failing open
 * quickly is much better than showing nothing.
 *
 * Note that this deadline is one-way. Once it fires, the provider never
 * registers its config-update listener, so a datafile arriving later will not
 * re-evaluate any flag for the rest of that page load.
 */
const DEFAULT_TIMEOUT_MS = 3000;

/**
 * Mount near the root of an app, inside any auth/config providers it needs.
 *
 * Apps supply the user themselves — typically
 * `cookieJar.getAccountId()` from `@consumer-frontend/api` — so this lib takes
 * no dependency on the api lib and cannot pull MSW or axios into a consumer.
 *
 * When Optimizely is switched off, unconfigured, or there is no authenticated
 * user, children render with no provider and every `useFeatureFlag` call
 * reports the flag as off. Logged-out users are expected to have been
 * redirected to CNUFE's login before reaching these apps, and CNUFE evaluates
 * its own maintenance flag.
 */
export const OptimizelyWrapper: OptimizelyWrapperT = ({
  children,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  user,
}) => {
  if (!optimizelyClient || !user?.id) {
    return children;
  }

  return (
    <OptimizelyProvider client={optimizelyClient} timeout={timeoutMs} user={user}>
      {children}
    </OptimizelyProvider>
  );
};
